import { Injectable } from '@angular/core';
import { debounceTime, distinctUntilChanged, filter, from, map, merge, Observable, of, OperatorFunction, startWith, switchMap, tap, zipAll } from 'rxjs';
import { ApiService } from './api.service';
import { MicrosoftService } from './microsoft.service';
import { MicrosoftDriveService } from './microsoftDrive.service';

interface SearchPartialResponse {
	index: number,
	isBeforeRequest: boolean,
	response?: any
}

export interface SearchResponse {
	index: number,
	length: number,
	isLast: boolean,
	isFirst: boolean,
	isBeforeRequest: boolean,
	response?: any
}

export enum SearchIndex {
	Users = 1,
    Etablissements = 2,
    Pages = 3,
	Drive = 4,
	Annuaire = 5,
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {

	private engines: {index: number, fn: (terms: string) => Observable<SearchPartialResponse>}[] = [
		{
            index: SearchIndex.Users,
            fn: terms => this.apiService.get(`salaries/search/${terms ?? ''}`).pipe(
                map(response => ({index: SearchIndex.Users, isBeforeRequest: false, response})),
				startWith({index: SearchIndex.Users, isBeforeRequest: true})
            )
        },
        {
            index: SearchIndex.Etablissements,
            fn: terms => this.apiService.get(`etablissements/search/${terms ?? ''}`).pipe(
                map(response => ({index: SearchIndex.Etablissements, isBeforeRequest: false, response})),
				startWith({index: SearchIndex.Etablissements, isBeforeRequest: true})
            )
        },
        {
            index: SearchIndex.Pages,
            fn: terms => this.apiService.get(`menu/search/${terms ?? ''}`).pipe(
                map(response => ({index: SearchIndex.Pages, isBeforeRequest: false, response})),
				startWith({index: SearchIndex.Pages, isBeforeRequest: true})
            )
        },
		{
			index: SearchIndex.Drive,
			fn: terms => from(
				this.microsoftService.isAuthenticated instanceof Observable ?
				this.microsoftService.isAuthenticated:
				of(this.microsoftService.isAuthenticated)
			).pipe(
				switchMap(allowed => 
					allowed ? 
					from([
						this.microsoftService
							.get(`drives/${this.microsoftDriveService.id}/root/search(q='${terms.replace(/'/g, '')}')`)
							.pipe(map(({value}) => value)),
						
						this.apiService.post(`drive/search`, {terms})
					]).pipe(
						zipAll(),
						map(([termsResponse, tagsResponse]) => [...termsResponse, ...tagsResponse].unique('id').slice(0, 19)),
						switchMap(value => this.microsoftService.batch(
							value.map(({id}) => ({id, method: 'GET', url: `drives/${this.microsoftDriveService.id}/items/${id}`}))
						))
					):
					of([])
				),
				map(response => ({
					index: 4,
					isBeforeRequest: false,
					response: response
								.map(value => value.value)
								.filter(value => !value.error)
								.sort((a, b) => a.name.localeCompare(b.name))
								.map(MicrosoftDriveService.withBreadcrumbs)
				})),
				startWith({
					index: 4,
					isBeforeRequest: true
				})
			)
		},
        {
            index: SearchIndex.Annuaire,
            fn: () => of({
				index: SearchIndex.Annuaire,
				isBeforeRequest: false,
				response: [{
                    name: "Annuaire des collaborateurs",
					link: `/search/users`
				}, {
                    name: "Annuaire des établissements",
					link: `/search/etablissements`
                }]
			}).pipe(startWith({
				index: SearchIndex.Annuaire,
				isBeforeRequest: true
			}))
        }
	];
	
	private counters: number[] = [-1, -1];

	state: {isWaiting?: boolean, terms?: string} = {};

	constructor(private apiService: ApiService,
				private microsoftService: MicrosoftService,
				private microsoftDriveService: MicrosoftDriveService) {}

	search (on: SearchIndex[] = [
		SearchIndex.Users,
		SearchIndex.Etablissements,
        SearchIndex.Annuaire,
		SearchIndex.Pages,
		SearchIndex.Drive
	], emptyTerms: boolean = false): OperatorFunction<string, SearchResponse> {

		const engines = this.engines.filter(({index}) => on.includes(index));
		const length = engines.length;

		return source => source.pipe(
			tap(terms => this.state.terms = terms),
			distinctUntilChanged(),
			debounceTime(500),
			filter(terms => emptyTerms ? true : terms?.length > 2),
            tap(() => {
				this.state.isWaiting = true
				this.counters = [-1, -1];
			}),
			switchMap(terms => merge(...engines.map(({fn}) => fn(terms)))),
			map(response => ({
				...response,
				length,
				isFirst: (response.isBeforeRequest ? ++this.counters[0] : ++this.counters[1]) === 0,
				isLast: (response.isBeforeRequest ? this.counters[0] : this.counters[1]) === length - 1
			})),
			tap(({isBeforeRequest, isLast}) => {
				if(!isBeforeRequest && isLast)
					this.state.isWaiting = false;
			})
		);
	}
}
